import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSliders,
  faCalendarDays,
  faCircleExclamation,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Title from "../shared/components/Title";
import SelectDropdown from "../shared/components/SelectDropdown";
import apiClient from "../utils/apiClient";
import { colors } from "../global";
import TotalConsignmentsCard from "../features/reporting-hub/TotalConsignmentsCard";
import QbrTableCard from "../features/reporting-hub/QbrTableCard";
import VisualizationBuilder from "../features/reporting-hub/VisualizationBuilder";
import {
  applyVisualizationFilters,
  buildTableRows,
  CHART_TYPE_OPTIONS,
  groupedSeries,
  resolveColumnCategory,
  resolveColumnMeta,
  resolveColumnType,
  resolveColumnUnit,
  simpleSeries,
  SORT_OPTIONS,
  TOP_N_OPTIONS,
  VIZ_CHART_OPTIONS,
} from "../features/reporting-hub/reportingHubUtils";

const DATE_RANGE_PRESETS = [
  { value: "last7Days", label: "Last 7 days" },
  { value: "last30Days", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "custom", label: "Custom range" },
];

const GRAPH_SECTIONS = [
  {
    key: "consignments",
    title: "Total Consignments",
    description: "Ranking consignments per customer.",
  },
  {
    key: "qbrTable",
    title: "QBR Table",
    description: "Detailed consignment rows for QBR reporting.",
  },
  {
    key: "visualization",
    title: "Visualization Builder",
    description: "Drag and drop fields to build custom charts.",
  },
];

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateRangeByPreset = (preset) => {
  const now = new Date();
  const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDate = new Date(endDate);

  if (preset === "last7Days") {
    startDate.setDate(endDate.getDate() - 6);
    return { from: toInputDate(startDate), to: toInputDate(endDate) };
  }

  if (preset === "last30Days") {
    startDate.setDate(endDate.getDate() - 29);
    return { from: toInputDate(startDate), to: toInputDate(endDate) };
  }

  if (preset === "thisMonth") {
    const monthStart = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    return { from: toInputDate(monthStart), to: toInputDate(endDate) };
  }

  if (preset === "lastMonth") {
    const monthStart = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1);
    const monthEnd = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
    return { from: toInputDate(monthStart), to: toInputDate(monthEnd) };
  }

  return { from: "", to: "" };
};

const ReportingHub = () => {
  const ON_TIME_ONLY_MODE = false;
  const [activeGraphSection, setActiveGraphSection] = useState(null);
  const [isConsignmentLoading, setIsConsignmentLoading] = useState(false);
  const [consignmentError, setConsignmentError] = useState("");
  const [consignmentData, setConsignmentData] = useState([]);
  const [chartType, setChartType] = useState("bar");
  const [topN, setTopN] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc");
  const [showConsignmentValues] = useState(true);
  const [vizTableRowsByName, setVizTableRowsByName] = useState({});
  const [isVizDataLoading, setIsVizDataLoading] = useState(false);
  const [vizDataError, setVizDataError] = useState("");
  const [vizChartData, setVizChartData] = useState(null);
  const [isVizChartLoading, setIsVizChartLoading] = useState(false);
  const [vizChartError, setVizChartError] = useState("");
  const [vizRawRows, setVizRawRows] = useState([]);
  const [vizAvailableColumns, setVizAvailableColumns] = useState([]);
  const [xCategoryFilters, setXCategoryFilters] = useState([]);
  const [vizChartResetKey, setVizChartResetKey] = useState(0);
  const [vizChartType, setVizChartType] = useState("line");
  const [vizAggregate, setVizAggregate] = useState("SUM");
  const VIZ_AGG_OPTIONS = [
    { label: "SUM", value: "SUM" },
    { label: "COUNT", value: "COUNT" },
    { label: "AVG", value: "AVG" },
  ];
  const [vizTopN, setVizTopN] = useState(10);
  const [vizSortOrder, setVizSortOrder] = useState("desc");
  const [vizMapping, setVizMapping] = useState({
    xField: null,
    yField: null,
    seriesField: null,
  });
  const [datePreset, setDatePreset] = useState("last30Days");
  const [dateFrom, setDateFrom] = useState(() => getDateRangeByPreset("last30Days").from);
  const [dateTo, setDateTo] = useState(() => getDateRangeByPreset("last30Days").to);
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => getDateRangeByPreset("last30Days").from);
  const [appliedDateTo, setAppliedDateTo] = useState(() => getDateRangeByPreset("last30Days").to);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [draftParentCustomer, setDraftParentCustomer] = useState("");
  const [draftChildCustomer, setDraftChildCustomer] = useState("");
  const [draftCarrierFilter, setDraftCarrierFilter] = useState("");
  const [draftServiceFilter, setDraftServiceFilter] = useState("");
  const [appliedParentCustomer, setAppliedParentCustomer] = useState("");
  const [appliedChildCustomer, setAppliedChildCustomer] = useState("");
  const [appliedCarrierFilter, setAppliedCarrierFilter] = useState("");
  const [appliedServiceFilter, setAppliedServiceFilter] = useState("");
  const [reportingHubFilterOptions, setReportingHubFilterOptions] = useState({
    parentCustomers: [],
    childCustomers: [],
    carriers: [],
    services: [],
  });
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState("");

  const isDateRangeInvalid =
    Boolean(dateFrom) &&
    Boolean(dateTo) &&
    new Date(dateFrom) > new Date(dateTo);

  const reportingHubChartFilters = useMemo(() => {
    return {
      customerIds: [],
      carrierIds: [],
      fromDate: appliedDateFrom || "",
      toDate: appliedDateTo || "",
      parentCustomer: appliedParentCustomer.trim(),
      childCustomer: appliedChildCustomer.trim(),
      carrier: appliedCarrierFilter.trim(),
      service: appliedServiceFilter.trim(),
    };
  }, [
    appliedCarrierFilter,
    appliedChildCustomer,
    appliedDateFrom,
    appliedDateTo,
    appliedParentCustomer,
    appliedServiceFilter,
  ]);

  const reportingHubBaseQueryFilters = useMemo(() => {
    return {
      from_date: appliedDateFrom || null,
      to_date: appliedDateTo || null,
      parent_customer: appliedParentCustomer.trim() || null,
      child_customer: appliedChildCustomer.trim() || null,
      carrier: appliedCarrierFilter.trim() || null,
      service: appliedServiceFilter.trim() || null,
    };
  }, [
    appliedCarrierFilter,
    appliedChildCustomer,
    appliedDateFrom,
    appliedDateTo,
    appliedParentCustomer,
    appliedServiceFilter,
  ]);

  const reportingHubBaseQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (reportingHubBaseQueryFilters.from_date) {
      params.set("from_date", reportingHubBaseQueryFilters.from_date);
    }
    if (reportingHubBaseQueryFilters.to_date) {
      params.set("to_date", reportingHubBaseQueryFilters.to_date);
    }
    if (reportingHubBaseQueryFilters.parent_customer) {
      params.set("parent_customer", reportingHubBaseQueryFilters.parent_customer);
    }
    if (reportingHubBaseQueryFilters.child_customer) {
      params.set("child_customer", reportingHubBaseQueryFilters.child_customer);
    }
    if (reportingHubBaseQueryFilters.carrier) {
      params.set("carrier", reportingHubBaseQueryFilters.carrier);
    }
    if (reportingHubBaseQueryFilters.service) {
      params.set("service", reportingHubBaseQueryFilters.service);
    }
    return params.toString();
  }, [reportingHubBaseQueryFilters]);

  const reportingHubLookupFilters = useMemo(
    () => ({
      fromDate: dateFrom || "",
      toDate: dateTo || "",
      parentCustomer: draftParentCustomer.trim(),
      childCustomer: draftChildCustomer.trim(),
      carrier: draftCarrierFilter.trim(),
      service: draftServiceFilter.trim(),
    }),
    [
      dateFrom,
      dateTo,
      draftCarrierFilter,
      draftChildCustomer,
      draftParentCustomer,
      draftServiceFilter,
    ]
  );

  const handleDatePresetChange = useCallback((preset) => {
    setDatePreset(preset);
    if (preset === "custom") {
      return;
    }
    const range = getDateRangeByPreset(preset);
    setDateFrom(range.from);
    setDateTo(range.to);
  }, []);

  useEffect(() => {
    if (!dateFrom || !dateTo || isDateRangeInvalid) {
      setIsFilterOptionsLoading(false);
      setReportingHubFilterOptions({
        parentCustomers: [],
        childCustomers: [],
        carriers: [],
        services: [],
      });
      setFilterOptionsError("");
      return;
    }

    const fetchFilterOptions = async () => {
      setIsFilterOptionsLoading(true);
      setFilterOptionsError("");
      try {
        const response = await apiClient.post(
          "/api/v1/reportingHub/filter-options",
          { filters: reportingHubLookupFilters }
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            err.detail || err.error || "Failed to load filter options"
          );
        }
        const payload = await response.json();
        setReportingHubFilterOptions({
          parentCustomers: payload.parentCustomers || [],
          childCustomers: payload.childCustomers || [],
          carriers: payload.carriers || [],
          services: payload.services || [],
        });
      } catch (err) {
        setFilterOptionsError(err.message);
        setReportingHubFilterOptions({
          parentCustomers: [],
          childCustomers: [],
          carriers: [],
          services: [],
        });
      } finally {
        setIsFilterOptionsLoading(false);
      }
    };

    fetchFilterOptions();
  }, [dateFrom, dateTo, isDateRangeInvalid, reportingHubLookupFilters]);

  useEffect(() => {
    if (
      draftParentCustomer &&
      !reportingHubFilterOptions.parentCustomers.includes(draftParentCustomer)
    ) {
      setDraftParentCustomer("");
    }
    if (
      draftChildCustomer &&
      !reportingHubFilterOptions.childCustomers.includes(draftChildCustomer)
    ) {
      setDraftChildCustomer("");
    }
    if (
      draftCarrierFilter &&
      !reportingHubFilterOptions.carriers.includes(draftCarrierFilter)
    ) {
      setDraftCarrierFilter("");
    }
    if (
      draftServiceFilter &&
      !reportingHubFilterOptions.services.includes(draftServiceFilter)
    ) {
      setDraftServiceFilter("");
    }
  }, [
    draftCarrierFilter,
    draftChildCustomer,
    draftParentCustomer,
    draftServiceFilter,
    reportingHubFilterOptions,
  ]);

  const parentCustomerOptions = useMemo(
    () =>
      reportingHubFilterOptions.parentCustomers.map((value) => ({
        value,
        label: value,
      })),
    [reportingHubFilterOptions.parentCustomers]
  );

  const childCustomerOptions = useMemo(
    () =>
      reportingHubFilterOptions.childCustomers.map((value) => ({
        value,
        label: value,
      })),
    [reportingHubFilterOptions.childCustomers]
  );

  const carrierFilterOptions = useMemo(
    () =>
      reportingHubFilterOptions.carriers.map((value) => ({
        value,
        label: value,
      })),
    [reportingHubFilterOptions.carriers]
  );

  const serviceFilterOptions = useMemo(
    () =>
      reportingHubFilterOptions.services.map((value) => ({
        value,
        label: value,
      })),
    [reportingHubFilterOptions.services]
  );

  useEffect(() => {
    if (!hasAppliedFilters) {
      setConsignmentData([]);
      setConsignmentError("");
      setIsConsignmentLoading(false);
      return;
    }

    if (ON_TIME_ONLY_MODE) {
      setConsignmentData([]);
      setConsignmentError("");
      return;
    }

    if (activeGraphSection !== "consignments") {
      setIsConsignmentLoading(false);
      return;
    }

    const fetchConsignments = async () => {
      setIsConsignmentLoading(true);
      setConsignmentError("");

      try {
        const endpoint = reportingHubBaseQuery
          ? `/api/v1/reportingHub/customers/total-consignments?${reportingHubBaseQuery}`
          : "/api/v1/reportingHub/customers/total-consignments";
        const response = await apiClient.get(endpoint);
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            err.detail || err.error || "Failed to load consignments"
          );
        }

        const payload = await response.json();
        if (payload?.error) {
          throw new Error(payload.error);
        }
        setConsignmentData(payload.customers || []);
      } catch (err) {
        setConsignmentError(err.message);
      } finally {
        setIsConsignmentLoading(false);
      }
    };

    fetchConsignments();
  }, [ON_TIME_ONLY_MODE, activeGraphSection, reportingHubBaseQuery, hasAppliedFilters]);


  const filteredConsignments = useMemo(() => {
    if (!consignmentData.length) {
      return [];
    }

    const sorted = [...consignmentData].sort((a, b) => {
      const delta = a.totalConsignments - b.totalConsignments;
      return sortOrder === "asc" ? delta : -delta;
    });
    const normalizedTopN =
      topN === "all" || topN == null || topN === "" ? null : Number(topN);
    return Number.isFinite(normalizedTopN) && normalizedTopN > 0
      ? sorted.slice(0, normalizedTopN)
      : sorted;
  }, [consignmentData, topN, sortOrder]);

  const chartPayload = useMemo(() => {
    const categories = filteredConsignments.map(
      (item) => item.customerName
    );
    const values = filteredConsignments.map(
      (item) => item.totalConsignments
    );

    if (chartType === "pie" || chartType === "donut") {
      return {
        series: values,
        options: {
          chart: {
            id: "consignments-chart",
            type: chartType,
            toolbar: { show: false },
          },
          labels: categories,
          dataLabels: {
            enabled: showConsignmentValues,
          },
          colors: [
            colors.primary,
            colors.secondary,
            colors.accent,
            colors.info,
            colors.success,
            colors.warning,
            colors.mutedBlue,
            colors.lightAccent,
          ],
          legend: { position: "bottom" },
          tooltip: {
            y: {
              formatter: (val) =>
                `${val} ${val === 1 ? "Consignment" : "Consignments"}`,
            },
          },
        },
      };
    }

    return {
      series: [
        {
          name: "Consignments",
          data: values,
        },
      ],
      options: {
        chart: {
          id: "consignments-chart",
          type: chartType,
          toolbar: { show: false },
          height: 320,
        },
        xaxis: {
          categories,
          labels: {
            rotate: -35,
            trim: true,
            style: { fontSize: "12px" },
          },
        },
        colors: [colors.primary],
        dataLabels: {
          enabled: showConsignmentValues,
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "50%",
          },
        },
        stroke: {
          curve: chartType === "line" || chartType === "area" ? "smooth" : "straight",
          width: chartType === "bar" ? 0 : 3,
        },
        fill: {
          opacity: chartType === "area" ? 0.35 : 1,
        },
        tooltip: {
          y: {
            formatter: (val) =>
              `${val} ${val === 1 ? "Consignment" : "Consignments"}`,
          },
        },
      },
    };
  }, [filteredConsignments, chartType, showConsignmentValues]);

  const vizTableSchemas = useMemo(
    () => [
      {
        name: "Data",
        columns: vizAvailableColumns,
      },
    ],
    [vizAvailableColumns]
  );

  const visualizationTables = useMemo(
    () =>
      vizTableSchemas.map((table) => ({
        ...table,
        rows: vizTableRowsByName[table.name] || [],
      })),
    [vizTableSchemas, vizTableRowsByName]
  );

  const vizTable = useMemo(
    () => visualizationTables[0] || { columns: [], rows: [] },
    [visualizationTables]
  );

  useEffect(() => {
    if (!hasAppliedFilters) {
      setVizAvailableColumns([]);
      setVizRawRows([]);
      setIsVizChartLoading(false);
      setVizDataError("");
      setVizChartData(null);
      setVizChartError("");
      setVizTableRowsByName({});
      return;
    }

    if (ON_TIME_ONLY_MODE) {
      setVizAvailableColumns([]);
      setVizRawRows([]);
      setIsVizChartLoading(false);
      setVizDataError("");
      setVizChartData(null);
      setVizChartError("");
      return;
    }

    if (activeGraphSection !== "visualization") {
      setIsVizDataLoading(false);
      return;
    }

    const fetchVisualizationData = async () => {
      setIsVizDataLoading(true);
      setVizDataError("");

      try {
        const payload = {
          filters: reportingHubChartFilters,
          metadataOnly: true,
        };

        const response = await apiClient.post(
          "/api/v1/reportingHub/chart",
          payload
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            err.detail || err.error || "Failed to load visualization data"
          );
        }
        const result = await response.json();
        const columns = result?.columns || result?.result?.columns || [];
        setVizAvailableColumns(
          Array.isArray(columns)
            ? columns
                .map((col) => {
                  if (!col) return null;
                  // New API returns {field, label} objects
                  if (typeof col === "object" && col.field) {
                    return {
                      key: col.field,
                      label: col.label,
                      type: resolveColumnType(col.label || col.field, col.field),
                      unit: resolveColumnUnit(col.label || col.field, col.field),
                      category: resolveColumnCategory(col.label || col.field),
                      normalizedKey: col.field,
                    };
                  }
                  // Fallback for plain string columns
                  return resolveColumnMeta(col);
                })
                .filter(Boolean)
            : []
        );
        setVizRawRows([]);
        setVizTableRowsByName({});
      } catch (err) {
        setVizDataError(err.message);
      } finally {
        setIsVizDataLoading(false);
      }
    };

    fetchVisualizationData();
  }, [
    ON_TIME_ONLY_MODE,
    activeGraphSection,
    hasAppliedFilters,
    reportingHubChartFilters,
  ]);

  useEffect(() => {
    if (!Array.isArray(vizRawRows) || !vizRawRows.length) {
      setVizTableRowsByName({});
      return;
    }
    const filteredRows = applyVisualizationFilters(vizRawRows);
    const nextRowsByName = vizTableSchemas.reduce((acc, table) => {
      acc[table.name] = buildTableRows(filteredRows, table);
      return acc;
    }, {});
    setVizTableRowsByName(nextRowsByName);
  }, [vizRawRows, vizTableSchemas]);

  useEffect(() => {
    setVizChartData(null);
    setVizChartError("");
  }, [vizMapping.xField, vizMapping.yField, vizMapping.seriesField, vizChartType]);

  useEffect(() => {
    if (
      !hasAppliedFilters ||
      ON_TIME_ONLY_MODE ||
      activeGraphSection !== "visualization" ||
      !vizMapping.xField ||
      !vizMapping.yField
    ) {
      setVizChartData(null);
      setVizRawRows([]);
      setVizChartError("");
      setIsVizChartLoading(false);
      return;
    }

    const fetchVisualizationChart = async () => {
      setIsVizChartLoading(true);
      setVizChartError("");

      try {
        const payload = {
          filters: reportingHubChartFilters,
          xField: vizMapping.xField,
          yField: vizMapping.yField,
          seriesField: vizMapping.seriesField,
          agg: vizAggregate || "SUM",
        };

        const response = await apiClient.post(
          "/api/v1/reportingHub/chart",
          payload
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            err.detail || err.error || "Failed to load visualization chart"
          );
        }

        const result = await response.json();
        setVizChartData({
          categories: result?.categories || result?.result?.categories || [],
          series: result?.series || result?.result?.series || [],
        });
        setVizRawRows([]);
      } catch (err) {
        setVizChartError(err.message);
        setVizChartData(null);
        setVizRawRows([]);
      } finally {
        setIsVizChartLoading(false);
      }
    };

    fetchVisualizationChart();
  }, [
    ON_TIME_ONLY_MODE,
    activeGraphSection,
    hasAppliedFilters,
    reportingHubChartFilters,
    vizChartType,
    vizAggregate,
    vizMapping.xField,
    vizMapping.yField,
    vizMapping.seriesField,
  ]);

  const findColumn = useCallback(
    (key) => vizTable?.columns.find((col) => col.key === key),
    [vizTable]
  );

  const clearVizMapping = useCallback(
    () =>
      setVizMapping({
        xField: null,
        yField: null,
        seriesField: null,
      }),
    []
  );

  useEffect(() => {
    clearVizMapping();
  }, [clearVizMapping]);

  useEffect(() => {
    setVizChartResetKey((prev) => prev + 1);
  }, [vizChartType]);

  const handleDropField = (target) => (event) => {
    event.preventDefault();
    const columnKey = event.dataTransfer.getData("text/plain");
    if (!columnKey) {
      return;
    }
    if (!findColumn(columnKey)) {
      return;
    }
    setVizMapping((prev) => ({
      ...prev,
      [target]: columnKey,
    }));
  };

  const allowDrop = useCallback((event) => event.preventDefault(), []);

  const vizChartPayload = useMemo(() => {
    let rows = vizTable?.rows || [];
    const xKey = vizMapping.xField;
    const yKey = vizMapping.yField;
    const groupKey = vizMapping.seriesField;

    const xCol = xKey ? findColumn(xKey) : null;
    const yCol = yKey ? findColumn(yKey) : null;
    const apiCategories = vizChartData?.categories;
    const apiSeries = vizChartData?.series;

    const invalid = (reason) => ({
      isValid: false,
      reason,
      chart: null,
    });

    const normalizeDateValue = (value) => {
      if (value == null || value === "") {
        return null;
      }

      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.getTime();
      }

      if (typeof value === "number" && Number.isFinite(value)) {
        const abs = Math.abs(value);
        if (abs > 1e15) {
          return Math.round(value / 1000);
        }
        if (abs > 1e10) {
          return Math.round(value);
        }
        if (abs > 1e8) {
          return Math.round(value * 1000);
        }
        return null;
      }

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    };

    const formatDateLabel = (value) => {
      const timestamp = normalizeDateValue(value);
      if (timestamp == null) {
        return value == null ? "" : String(value);
      }
      return new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(timestamp));
    };

    const formatCategoryValue = (value) => {
      if (value === true) {
        return "Yes";
      }
      if (value === false) {
        return "No";
      }
      if (value == null || value === "") {
        return "Unknown";
      }
      return String(value);
    };

    if (!rows.length && (!apiCategories || !apiSeries)) {
      return invalid("No data available.");
    }

    if (!xKey || !yKey) {
      return invalid("Drop at least one field into X and Y.");
    }

    const selectedXValues = new Set(
      (xCategoryFilters || []).map((item) => String(item.value))
    );
    if (xCol?.type === "category" && selectedXValues.size > 0) {
      rows = rows.filter((row) =>
        selectedXValues.has(String(row[xKey]))
      );
    }

    const formatAxisTitle = (col, key) => {
      const label = col?.label || key || "";
      if (!label) {
        return "";
      }
      return col?.unit ? `${label} (${col.unit})` : label;
    };

    const defaultAxisLabelStyle = {
      fontSize: "12px",
    };

    const defaultXAxisLabels = {
      style: defaultAxisLabelStyle,
    };

    const defaultYAxisLabels = {
      style: defaultAxisLabelStyle,
    };

    const baseChart = {
      options: {
        chart: { id: "visualization-lab", toolbar: { show: false } },
        colors: [
          colors.primary,
          colors.secondary,
          colors.accent,
          colors.info,
          colors.success,
          colors.warning,
        ],
        dataLabels: { enabled: true },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val) =>
              yCol?.unit ? `${val} ${yCol.unit}` : `${val}`,
          },
        },
      },
      series: [],
    };

    if (
      ["scatter", "bubble"].includes(vizChartType) &&
      (xCol?.type !== "number" || yCol?.type !== "number")
    ) {
      return invalid("Scatter/Bubble membutuhkan X & Y numeric.");
    }

    if (vizChartType === "candlestick") {
      const needed = ["open", "high", "low", "close"];
      const missing = needed.filter(
        (key) => !vizTable.columns.find((col) => col.key === key)
      );
      if (missing.length) {
        return invalid("Candlestick membutuhkan kolom open/high/low/close.");
      }
      const seriesData = rows.map((row) => ({
        x: row[xKey],
        y: [row.open, row.high, row.low, row.close],
      }));
      return {
        isValid: true,
        chart: {
          series: [{ data: seriesData }],
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "candlestick" },
            xaxis: {
              type: xCol?.type === "date" ? "datetime" : "category",
              labels: defaultXAxisLabels,
            },
            yaxis: {
              labels: defaultYAxisLabels,
            },
          },
        },
      };
    }

    if (vizChartType === "boxPlot") {
      const seriesData = rows.map((row) => {
        const low = row.minTransit ?? 0;
        const high = row.maxTransit ?? 0;
        const median = (low + high) / 2;
        const q1 = low + (high - low) * 0.25;
        const q3 = low + (high - low) * 0.75;
        return {
          x: row[xKey],
          y: [low, q1, median, q3, high],
        };
      });
      return {
        isValid: true,
        chart: {
          series: [{ data: seriesData }],
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "boxPlot" },
            xaxis: {
              type: xCol?.type === "date" ? "datetime" : "category",
              labels: defaultXAxisLabels,
            },
            yaxis: {
              labels: defaultYAxisLabels,
            },
          },
        },
      };
    }

    if (vizChartType === "bubble") {
      const bubbleSizeKey = yKey;
      const seriesData = rows.map((row) => [
        row[xKey],
        row[yKey],
        row[bubbleSizeKey],
      ]);
      const tooltipLabel = (row) =>
        row?.customerName ||
        row?.customer ||
        row?.customerNk ||
        row?.customer_id ||
        row?.customerId ||
        row?.[xKey];
      return {
        isValid: true,
        chart: {
          series: [
            {
              name: yCol?.label || "Value",
              data: seriesData,
            },
          ],
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "bubble" },
            xaxis: {
              type: "numeric",
              labels: defaultXAxisLabels,
            },
            yaxis: {
              labels: defaultYAxisLabels,
            },
            tooltip: {
              custom: ({ dataPointIndex }) => {
                const row = rows[dataPointIndex] || {};
                const name = tooltipLabel(row);
                const xVal = row?.[xKey];
                const yVal = row?.[yKey];
                const zVal = row?.[bubbleSizeKey];
                const xUnit = xCol?.unit;
                const yUnit = yCol?.unit;
                const zUnit = yCol?.unit;
                return [
                  "<div class='apexcharts-tooltip-title'>",
                  name ? String(name) : "Item",
                  "</div>",
                  `<div class='apexcharts-tooltip-text'>X: ${xVal ?? "-"}${
                    xUnit ? ` ${xUnit}` : ""
                  }</div>`,
                  `<div class='apexcharts-tooltip-text'>Y: ${yVal ?? "-"}${
                    yUnit ? ` ${yUnit}` : ""
                  }</div>`,
                  `<div class='apexcharts-tooltip-text'>Size: ${zVal ?? "-"}${
                    zUnit ? ` ${zUnit}` : ""
                  }</div>`,
                ].join("");
              },
            },
          },
        },
      };
    }

    if (vizChartType === "scatter") {
      const seriesData = rows.map((row) => [row[xKey], row[yKey]]);
      const tooltipLabel = (row) =>
        row?.customerName ||
        row?.customer ||
        row?.customerNk ||
        row?.customer_id ||
        row?.customerId ||
        row?.[xKey];
      return {
        isValid: true,
        chart: {
          series: [
            {
              name: yCol?.label || "Value",
              data: seriesData,
            },
          ],
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "scatter" },
            xaxis: {
              type: "numeric",
              labels: defaultXAxisLabels,
            },
            yaxis: {
              labels: defaultYAxisLabels,
            },
            tooltip: {
              custom: ({ dataPointIndex }) => {
                const row = rows[dataPointIndex] || {};
                const name = tooltipLabel(row);
                const xVal = row?.[xKey];
                const yVal = row?.[yKey];
                const xUnit = xCol?.unit;
                const yUnit = yCol?.unit;
                return [
                  "<div class='apexcharts-tooltip-title'>",
                  name ? String(name) : "Item",
                  "</div>",
                  `<div class='apexcharts-tooltip-text'>X: ${xVal ?? "-"}${
                    xUnit ? ` ${xUnit}` : ""
                  }</div>`,
                  `<div class='apexcharts-tooltip-text'>Y: ${yVal ?? "-"}${
                    yUnit ? ` ${yUnit}` : ""
                  }</div>`,
                ].join("");
              },
            },
          },
        },
      };
    }

    const shouldUseApiSeries =
      Array.isArray(apiCategories) &&
      Array.isArray(apiSeries) &&
      ![
        "candlestick",
        "boxPlot",
        "bubble",
        "scatter",
        "rangeArea",
        "rangeBar",
      ].includes(vizChartType);

    const { categories, series } = shouldUseApiSeries
      ? { categories: apiCategories, series: apiSeries }
      : groupKey
      ? groupedSeries(rows, xKey, yKey, groupKey)
      : (() => {
          const basic = simpleSeries(rows, xKey, yKey);
          return {
            categories: basic.categories,
            series: [
              { name: yCol?.label || "Value", data: basic.values },
            ],
          };
        })();

    const canApplyOrder = ![
      "scatter",
      "bubble",
      "candlestick",
      "boxPlot",
      "rangeArea",
      "rangeBar",
    ].includes(vizChartType);

    const applyOrderAndLimit = (rawCategories, rawSeries) => {
      if (!canApplyOrder || !rawCategories?.length || !rawSeries?.length) {
        return { categories: rawCategories, series: rawSeries };
      }
      const totals = rawCategories.map((_, index) =>
        rawSeries.reduce(
          (sum, item) => sum + (Number(item.data?.[index]) || 0),
          0
        )
      );
      const indexed = rawCategories.map((label, index) => ({
        label,
        index,
        total: totals[index] ?? 0,
      }));
      const nonZero = indexed.filter((item) => item.total !== 0);
      if (!nonZero.length) {
        const fallback =
          indexed.length > 0
            ? indexed
            : rawCategories.map((label, index) => ({
                label,
                index,
                total: totals[index] ?? 0,
              }));
        const normalizedTopN =
          vizTopN === "all" || vizTopN == null || vizTopN === ""
            ? null
            : Number(vizTopN);
        const limitedFallback =
          Number.isFinite(normalizedTopN) && normalizedTopN > 0
            ? fallback.slice(0, normalizedTopN)
            : fallback;
        return {
          categories: limitedFallback.map((item) => item.label),
          series: rawSeries.map((item) => ({
            ...item,
            data: limitedFallback.map(
              (entry) => item.data?.[entry.index] ?? 0
            ),
          })),
        };
      }
      nonZero.sort((a, b) => {
        const delta = a.total - b.total;
        return vizSortOrder === "asc" ? delta : -delta;
      });
      const normalizedTopN =
        vizTopN === "all" || vizTopN == null || vizTopN === ""
          ? null
          : Number(vizTopN);
      const limited =
        Number.isFinite(normalizedTopN) && normalizedTopN > 0
          ? nonZero.slice(0, normalizedTopN)
          : nonZero;
      const nextCategories = limited.map((item) => item.label);
      const nextSeries = rawSeries.map((item) => ({
        ...item,
        data: limited.map((entry) => item.data?.[entry.index] ?? 0),
      }));
      return { categories: nextCategories, series: nextSeries };
    };

    const ordered = applyOrderAndLimit(categories, series);
    const filteredSeries = ordered.series.filter((item) =>
      (item.data || []).some((value) => Number(value) !== 0)
    );
    const finalSeries = filteredSeries.length ? filteredSeries : ordered.series;

    const axisTitleX = formatAxisTitle(xCol, xKey);
    const axisTitleY = formatAxisTitle(yCol, yKey);
    const isHorizontal = vizChartType === "bar";
    const displayCategories =
      xCol?.type === "date"
        ? ordered.categories.map((value) => formatDateLabel(value))
        : ordered.categories.map((value) => formatCategoryValue(value));
    const normalizedCategories =
      xCol?.type === "date" && !isHorizontal
        ? ordered.categories.map((value) => normalizeDateValue(value) ?? value)
        : displayCategories;
    const horizontalCategories =
      xCol?.type === "date"
        ? ordered.categories.map((value) => formatDateLabel(value))
        : displayCategories;

    const baseOptions = {
      ...baseChart.options,
      ...(isHorizontal
        ? {
            xaxis: {
              categories: horizontalCategories,
              title: {
                text: axisTitleY,
                style: { fontWeight: 600 },
              },
              labels: defaultXAxisLabels,
            },
            yaxis: {
              title: {
                text: axisTitleX,
                style: { fontWeight: 600 },
              },
              labels: defaultYAxisLabels,
            },
          }
        : {
            xaxis: {
              categories: normalizedCategories,
              type: xCol?.type === "date" ? "datetime" : "category",
              title: {
                text: axisTitleX,
                style: { fontWeight: 600 },
              },
              labels:
                xCol?.type === "date"
                  ? {
                      ...defaultXAxisLabels,
                      formatter: (value) => formatDateLabel(value),
                    }
                  : defaultXAxisLabels,
            },
            yaxis: {
              title: {
                text: axisTitleY,
                style: { fontWeight: 600 },
              },
              labels: defaultYAxisLabels,
            },
          }),
    };

    if (["pie", "radialBar", "polarArea"].includes(vizChartType)) {
      const values = finalSeries[0]?.data || [];
      return {
        isValid: true,
        chart: {
          series: values,
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: vizChartType },
            labels: displayCategories,
          },
        },
      };
    }

    if (vizChartType === "treemap") {
      const values = finalSeries[0]?.data || [];
      const treemapData = ordered.categories.map((label, index) => ({
        x: displayCategories[index] ?? formatCategoryValue(label),
        y: values[index] ?? 0,
      }));
      return {
        isValid: true,
        chart: {
          series: [{ data: treemapData }],
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "treemap" },
          },
        },
      };
    }

    if (vizChartType === "heatmap") {
      const heatSeries = finalSeries.map((item) => ({
        name: item.name,
        data: ordered.categories.map((category, index) => ({
          x: displayCategories[index] ?? formatCategoryValue(category),
          y: item.data[index] ?? 0,
        })),
      }));
      return {
        isValid: true,
        chart: {
          series: heatSeries,
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "heatmap" },
          },
        },
      };
    }

    if (vizChartType === "funnel") {
      return {
        isValid: true,
        chart: {
          series: finalSeries[0]?.data || [],
          options: {
            ...baseChart.options,
            chart: { ...baseChart.options.chart, type: "bar" },
            plotOptions: {
              bar: {
                horizontal: true,
                isFunnel: true,
              },
            },
            xaxis: {
              categories: displayCategories,
              labels: defaultXAxisLabels,
            },
            yaxis: {
              labels: defaultYAxisLabels,
            },
            dataLabels: { enabled: true },
          },
        },
      };
    }

    const standardType =
      vizChartType === "column" ? "bar" : vizChartType;

    if (vizChartType === "mixed") {
      const mixedSeries = finalSeries.map((item, index) => ({
        ...item,
        type: index % 2 === 0 ? "column" : "line",
      }));
      return {
        isValid: true,
        chart: {
          series: mixedSeries,
          options: {
            ...baseOptions,
            chart: { ...baseChart.options.chart, type: "line" },
          },
        },
      };
    }

    if (vizChartType === "slope") {
      return {
        isValid: true,
        chart: {
          series: finalSeries,
          options: {
            ...baseOptions,
            chart: { ...baseChart.options.chart, type: "line" },
            stroke: { width: 3 },
            markers: { size: 4 },
          },
        },
      };
    }

    return {
      isValid: true,
      chart: {
        series: finalSeries,
        options: {
          ...baseOptions,
          chart: { ...baseChart.options.chart, type: standardType },
          plotOptions: isHorizontal
            ? { bar: { horizontal: true } }
            : { bar: { borderRadius: 6, columnWidth: "50%" } },
        },
      },
    };
  }, [
    vizChartType,
    vizMapping,
    vizTable,
    xCategoryFilters,
    vizTopN,
    vizSortOrder,
    findColumn,
    vizChartData,
  ]);

  const xCategoryOptions = useMemo(() => {
    const xKey = vizMapping.xField;
    const xCol = xKey ? findColumn(xKey) : null;
    if (!xKey || xCol?.type !== "category") {
      return [];
    }
    const sourceValues = (vizTable?.rows || []).length
      ? (vizTable?.rows || []).map((row) => row[xKey])
      : vizChartData?.categories || [];
    const values = Array.from(new Set(sourceValues)).filter(
      (value) => value !== undefined && value !== null
    );
    const formatCategoryOptionLabel = (value) => {
      if (value === true) {
        return "Yes";
      }
      if (value === false) {
        return "No";
      }
      if (value == null || value === "") {
        return "Unknown";
      }
      return String(value);
    };
    return values.map((value) => ({
      value,
      label: formatCategoryOptionLabel(value),
    }));
  }, [vizMapping.xField, vizTable, findColumn, vizChartData]);

  useEffect(() => {
    setXCategoryFilters([]);
  }, [vizMapping.xField]);

  const handleApplyFilters = useCallback(() => {
    if (isDateRangeInvalid) {
      return;
    }

    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedParentCustomer(draftParentCustomer);
    setAppliedChildCustomer(draftChildCustomer);
    setAppliedCarrierFilter(draftCarrierFilter);
    setAppliedServiceFilter(draftServiceFilter);
    setActiveGraphSection(null);
    setHasAppliedFilters(true);
  }, [
    dateFrom,
    dateTo,
    draftCarrierFilter,
    draftChildCustomer,
    draftParentCustomer,
    draftServiceFilter,
    isDateRangeInvalid,
  ]);

  return (
    <>
      <div className="p-6 pb-0">
        <Title title={"Reporting Hub"} />
      </div>

      {/* ── Filter Card ─────────────────────────────────────────── */}
      <div className="m-4 overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#144677] to-[#1a5c99] px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faSliders} className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-base leading-tight">Filters</h2>
            <p className="text-white/60 text-xs mt-0.5">Select a date range and database-backed filters to load data</p>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white px-6 py-5">
          <div className="grid grid-cols-1 gap-6">
            {/* ── Date Range ── */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faCalendarDays} className="w-3.5 h-3.5 text-[#144677]" />
                <label className="text-sm font-semibold text-gray-700">Date Range</label>
              </div>

              {/* Preset pills */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {DATE_RANGE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handleDatePresetChange(preset.value)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                      datePreset === preset.value
                        ? "bg-[#144677] text-white border-[#144677] shadow-sm"
                        : "bg-white text-gray-600 border-gray-200 hover:border-[#144677]/50 hover:text-[#144677]"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Date inputs */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">From</label>
                  <input
                    type="date"
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677] transition-colors ${
                      isDateRangeInvalid ? "border-red-400 bg-red-50" : "border-gray-200"
                    }`}
                    value={dateFrom}
                    onChange={(e) => { setDatePreset("custom"); setDateFrom(e.target.value); }}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">To</label>
                  <input
                    type="date"
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677] transition-colors ${
                      isDateRangeInvalid ? "border-red-400 bg-red-50" : "border-gray-200"
                    }`}
                    value={dateTo}
                    onChange={(e) => { setDatePreset("custom"); setDateTo(e.target.value); }}
                  />
                </div>
              </div>

              {isDateRangeInvalid && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                  <FontAwesomeIcon icon={faCircleExclamation} className="w-3 h-3 flex-shrink-0" />
                  "From" date cannot be later than "To".
                </div>
              )}
            </div>
          </div>

	          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Parent Customer
              </label>
              <SelectDropdown
                options={parentCustomerOptions}
                isDisabled={isFilterOptionsLoading}
                isLoading={isFilterOptionsLoading}
                loadingMessage="Loading parent customers..."
                value={
                  parentCustomerOptions.find(
                    (option) => option.value === draftParentCustomer
                  ) || null
                }
                onChange={(value) =>
                  setDraftParentCustomer(value?.value || "")
                }
                placeholder="All parent customers"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Child Customer
              </label>
              <SelectDropdown
                options={childCustomerOptions}
                isDisabled={isFilterOptionsLoading}
                isLoading={isFilterOptionsLoading}
                loadingMessage="Loading child customers..."
                value={
                  childCustomerOptions.find(
                    (option) => option.value === draftChildCustomer
                  ) || null
                }
                onChange={(value) => setDraftChildCustomer(value?.value || "")}
                placeholder="All child customers"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Carrier
              </label>
              <SelectDropdown
                options={carrierFilterOptions}
                isDisabled={isFilterOptionsLoading}
                isLoading={isFilterOptionsLoading}
                loadingMessage="Loading carriers..."
                value={
                  carrierFilterOptions.find(
                    (option) => option.value === draftCarrierFilter
                  ) || null
                }
                onChange={(value) =>
                  setDraftCarrierFilter(value?.value || "")
                }
                placeholder="All carriers"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Service
              </label>
              <SelectDropdown
                options={serviceFilterOptions}
                isDisabled={isFilterOptionsLoading}
                isLoading={isFilterOptionsLoading}
                loadingMessage="Loading services..."
                value={
                  serviceFilterOptions.find(
                    (option) => option.value === draftServiceFilter
                  ) || null
                }
                onChange={(value) =>
                  setDraftServiceFilter(value?.value || "")
                }
                placeholder="All services"
              />
            </div>
          </div>

          {isFilterOptionsLoading && !filterOptionsError && (
            <div className="mt-3 flex items-center gap-2 text-xs text-[#144677]">
              <FontAwesomeIcon
                icon={faSpinner}
                className="w-3 h-3 flex-shrink-0 animate-spin"
              />
              Loading filter options...
            </div>
          )}

          {filterOptionsError && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-red-600">
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="w-3 h-3 flex-shrink-0"
              />
              {filterOptionsError}
            </div>
          )}

          {/* Apply button */}
          <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={handleApplyFilters}
              disabled={isDateRangeInvalid || isFilterOptionsLoading}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#144677] hover:bg-[#1a5c99] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isFilterOptionsLoading && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="w-3.5 h-3.5 animate-spin"
                />
              )}
              {isFilterOptionsLoading ? "Loading Filters..." : "Apply Filters"}
            </button>
          </div>
        </div>
      </div>

      {!hasAppliedFilters && (
        <div className="mx-4 rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          Select a date range and filters, then click Apply to load a graph.
        </div>
      )}

      {hasAppliedFilters && (
        <div className="m-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Choose Graph
              </h2>
              <p className="text-sm text-gray-500">
                A graph will load only after you select it.
              </p>
            </div>
            {activeGraphSection && (
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                onClick={() => setActiveGraphSection(null)}
              >
                Back to menu
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {GRAPH_SECTIONS.filter(
              (section) => !ON_TIME_ONLY_MODE || section.key !== "consignments"
            ).map((section) => {
              const isActive = activeGraphSection === section.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveGraphSection(section.key)}
                  className={`rounded-2xl border p-5 text-left transition-all ${
                    isActive
                      ? "border-[#144677] bg-[#144677] text-white shadow-lg"
                      : "border-gray-200 bg-gradient-to-br from-white to-gray-50 text-gray-800 hover:border-[#144677]/40 hover:shadow-md"
                  }`}
                >
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] opacity-70">
                    Graph Option
                  </p>
                  <h3 className="text-lg font-semibold">{section.title}</h3>
                  <p className={`mt-2 text-sm ${isActive ? "text-white/80" : "text-gray-500"}`}>
                    {section.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {hasAppliedFilters && !ON_TIME_ONLY_MODE && activeGraphSection === "consignments" && (
        <TotalConsignmentsCard
          topN={topN}
          setTopN={setTopN}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          chartType={chartType}
          setChartType={setChartType}
          chartTypeOptions={CHART_TYPE_OPTIONS}
          topNOptions={TOP_N_OPTIONS}
          sortOptions={SORT_OPTIONS}
          isConsignmentLoading={isConsignmentLoading}
          consignmentError={consignmentError}
          filteredConsignments={filteredConsignments}
          chartPayload={chartPayload}
        />
      )}

      {hasAppliedFilters && activeGraphSection === "qbrTable" && (
        <QbrTableCard
          fromDate={appliedDateFrom}
          toDate={appliedDateTo}
          parentCustomer={appliedParentCustomer}
          childCustomer={appliedChildCustomer}
          carrier={appliedCarrierFilter}
          service={appliedServiceFilter}
        />
      )}

      {hasAppliedFilters && !ON_TIME_ONLY_MODE && activeGraphSection === "visualization" && (
        <VisualizationBuilder
          vizChartOptions={VIZ_CHART_OPTIONS}
          vizChartType={vizChartType}
          setVizChartType={setVizChartType}
          clearVizMapping={clearVizMapping}
          visualizationTables={visualizationTables}
          isVizDataLoading={isVizDataLoading}
          vizDataError={vizDataError}
          availableColumns={vizAvailableColumns}
          showTableSelectors={false}
          xCategoryOptions={xCategoryOptions}
          xCategoryFilters={xCategoryFilters}
          setXCategoryFilters={setXCategoryFilters}
          vizTopN={vizTopN}
          setVizTopN={setVizTopN}
          vizSortOrder={vizSortOrder}
          setVizSortOrder={setVizSortOrder}
          topNOptions={TOP_N_OPTIONS}
          sortOptions={SORT_OPTIONS}
          vizMapping={vizMapping}
          setVizMapping={setVizMapping}
          vizAggregate={vizAggregate}
          setVizAggregate={setVizAggregate}
          vizAggregateOptions={VIZ_AGG_OPTIONS}
          findColumn={findColumn}
          handleDropField={handleDropField}
          allowDrop={allowDrop}
          vizChartPayload={vizChartPayload}
          isVizChartLoading={isVizChartLoading}
          vizChartError={vizChartError}
          vizChartResetKey={vizChartResetKey}
        />
      )}
    </>
  );
};

export default ReportingHub;
